import { Dayjs } from 'dayjs';
import * as ResponseTypes from './types';
import { MyWindow } from 'react-app-env';
import { BaseResponse, Book } from './types';
import { BookToBeCreated } from 'pages/book';
import { LoginFormParams } from 'pages/login';
import { HttpClient } from './http-client-interceptor';
import { AssignProjectPostParams } from 'components/assign-project';
import { EffortConfirmationPostParams } from 'pages/confirm-effort';
import { ProjectStepItem } from 'pages/project-step-managing/types';
import { AddProjectStepListItem } from 'pages/project-plan-generate/types';
import { ChangePasswordPostParams } from 'components/change-password-dialog';
import { CreateRoleFormParams } from 'pages/roles/components/create-role-dialog';
import { CreateUserFormParams } from 'pages/users/components/create-user-dialog';
import { UpdateRoleFormParams } from 'pages/roles/components/update-role-dialog';
import { UpdateUserPostParams } from 'pages/users/components/update-user-dialog';
import { CreateProjectFormParams } from 'pages/projects/components/create-project-dialog';
import { UpdateProjectFormParams } from 'pages/projects/components/update-project-dialog';
import { CreateActivityTypeFormParams } from 'pages/activity-types/components/create-activity-type-dialog';
import { UpdateActivityTypeFormParams } from 'pages/activity-types/components/update-activity-type-dialog';
import { GetEffortRequestsFormParams, UpdateEffortRequestPostFormParams } from 'pages/effort-tracing/types';
import { CreateEffortContactFormParams } from 'pages/effort-contacts/components/create-effort-contact-dialog';
import { UpdateEffortContactFormParams } from 'pages/effort-contacts/components/update-effort-contact-dialog';
import { CreateEffortRequestPostFormParams } from 'pages/effort-tracing/components/create-effort-request-dialog';
import { UpdateProjectStepPostFormParams } from 'pages/project-step-managing/components/update-project-step-dialog';
import { CreateProjectStepPostFormParams } from 'pages/project-step-managing/components/create-project-step-dialog';
import { CreateActivityBoardItemPostFormParams } from 'pages/activity-managing/components/create-activity-board-item-dialog';
import { UpdateActivityBoardItemPostFormParams } from 'pages/activity-managing/components/update-activity-board-item-dialog';
import { UpdateProductProjectPlanPostFormParams } from 'pages/product-project-plan/components/update-product-project-plan-dialog';
import { CreateProductProjectPlanPostFormParams } from 'pages/product-project-plan/components/create-product-project-plan-dialog';
import { CreateEffortStatusContactPostFormParams } from 'pages/effort-status-contacts/components/create-effort-status-contact-dialog';
import { UpdateEffortStatusContactPostFormParams } from 'pages/effort-status-contacts/components/update-effort-status-contact-dialog';

declare const window: MyWindow;
const PUBLIC_FEEDBACK_REQUEST_TIMEOUT_MS = 15_000;

class Service extends HttpClient {
  private static instance?: Service;

  private constructor() {
    super(process.env.REACT_APP_REST_URL || window.config.REACT_APP_REST_URL);
  }

  public static getInstance() {
    if (!this.instance) {
      this.instance = new Service();
    }

    return this.instance;
  }

  // Account
  public sendCode = (data: LoginFormParams): Promise<BaseResponse<{}>> => this.instance.post(`/Account/SendCode`, data);

  public signOn = (
    data: LoginFormParams & {
      code: string;
    }
  ): Promise<BaseResponse<ResponseTypes.Credentials>> => this.instance.post('/Account/SignOn', data);

  public signIn = (data: { nameSurname: string; email: string; password: string }): Promise<BaseResponse<{}>> =>
    this.instance.post('/Account/SignIn', data);

  public forgotPassword = (email: string): Promise<ResponseTypes.ForgotPasswordGeneralResponse> =>
    this.instance.post('/Account/ForgotPassword', { email });

  public verifyResetCode = (email: string, code: string): Promise<ResponseTypes.ForgotPasswordGeneralResponse> =>
    this.instance.post('/Account/VerifyResetCode', { email, code });

  public resetPassword = (
    email: string,
    code: string,
    newPassword: string
  ): Promise<ResponseTypes.ForgotPasswordGeneralResponse> =>
    this.instance.post('/Account/ResetPassword', { email, code, newPassword });

  // Freshdesk
  public getFreshdeskDashboard = (take: number): Promise<BaseResponse<ResponseTypes.FreshdeskDashboardResponse>> =>
    this.instance.get(`/Freshdesk/Dashboard?Take=${take}`);

  public getFreshdeskFeedbackList = (
    filters: ResponseTypes.FreshdeskFeedbackListFilters,
    signal?: AbortSignal
  ): Promise<BaseResponse<ResponseTypes.FreshdeskFeedbackListItem[]>> => {
    const params = {
      ...(filters.responderId === null ? {} : { ResponderId: filters.responderId }),
      ...(filters.startDate === null ? {} : { StartDate: filters.startDate }),
      ...(filters.endDate === null ? {} : { EndDate: filters.endDate }),
      ...(filters.minRate === null ? {} : { MinRate: filters.minRate }),
      ...(filters.maxRate === null ? {} : { MaxRate: filters.maxRate }),
    };

    return this.instance.get('/FreshdeskFeedback/List', {
      params,
      signal,
      suppressErrorNotification: true,
    });
  };

  public getFreshdeskResponders = (signal?: AbortSignal): Promise<BaseResponse<ResponseTypes.FreshdeskResponder[]>> =>
    this.instance.get('/Freshdesk/ResponderIds', { signal, suppressErrorNotification: true });

  public getFreshdeskFeedback = (
    uniqueKey: string,
    signal?: AbortSignal
  ): Promise<BaseResponse<ResponseTypes.FreshdeskFeedbackTicket>> =>
    this.instance.get('/FreshdeskFeedback/Get', {
      params: { UniqueKey: uniqueKey },
      signal,
      timeout: PUBLIC_FEEDBACK_REQUEST_TIMEOUT_MS,
      isPublicRequest: true,
      suppressErrorNotification: true,
    });

  public submitFreshdeskFeedback = (
    data: ResponseTypes.FreshdeskFeedbackRequest,
    signal?: AbortSignal
  ): Promise<BaseResponse<{}>> =>
    this.instance.post('/FreshdeskFeedback/Feedback', data, {
      signal,
      timeout: PUBLIC_FEEDBACK_REQUEST_TIMEOUT_MS,
      isPublicRequest: true,
      suppressErrorNotification: true,
    });

  // Activity Board Item
  public createActivityBoardItem = (data: CreateActivityBoardItemPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityBoardItem/Add', data);

  public updateActivityBoardItem = (data: UpdateActivityBoardItemPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityBoardItem/Update', data);

  public deleteActivityBoardItem = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityBoardItem/Delete', { id });

  public getActivityBoardItems = (data: {
    bookId: number | null;
    projectId: number | null;
    userId: number | null;
    startDate: string | null;
    endDate: string | null;
  }): Promise<BaseResponse<ResponseTypes.ActivityBoardItem[]>> => this.instance.post('/ActivityBoardItem/List', data);

  // Activity Type
  public createActivityType = (data: CreateActivityTypeFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityType/Add', data);

  public updateActivityType = (data: UpdateActivityTypeFormParams & { id: number }): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityType/Update', data);

  public deleteActivityType = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ActivityType/Delete', { id });

  public getActivityTypes = (): Promise<ResponseTypes.GetActivityTypesResponse> =>
    this.instance.get('/ActivityType/List');

  public getSpecificActivityType = (id: number): Promise<ResponseTypes.GetSpecificActivityTypeResponse> =>
    this.instance.get(`/ActivityType/Get?Id=${id}`);

  // AI
  public getAiEffortEstimate = (data: { projectId: number; explanation: string }): Promise<BaseResponse<number>> =>
    this.instance.post('/ai/Estimate', data);

  // Chat
  public sendChatMessage = (data: {
    sessionId: string;
    message: string;
  }): Promise<BaseResponse<ResponseTypes.ChatSendResponse>> => this.instance.post('/Chat/Send', data);

  // Book
  public getSpecificBook = (companyId: number): Promise<ResponseTypes.GetSpecificBookResponse> =>
    this.instance.get(`/Book/${companyId}`);

  public createBook = (book: BookToBeCreated): Promise<BaseResponse<{}>> => this.instance.post('/Book/Add', book);

  public editBook = (book: Book): Promise<BaseResponse<{}>> => this.instance.post('/Book/Update', book);

  public deleteBook = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/Book/Delete', { id });

  public getVersionsByBookId = (bookId: number): Promise<ResponseTypes.GetVersionsByBookIdResponse> =>
    this.instance.get(`/Book/Versions?BookId=${bookId}`);

  public getComparasionsByVersionId = (versionId: number): Promise<ResponseTypes.GetComparasionsByVersionIdResponse> =>
    this.instance.get(`/Book/VersionCompare?VersionId=${versionId}`);

  public restoreVersion = (versionId: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/Book/VersionRestore', { versionId });

  // Company
  public getCompanies = (): Promise<ResponseTypes.GetShortBooksResponse> => this.instance.get('/Company');

  // Connection Types
  public createConnectionType = (title: string): Promise<BaseResponse<{}>> =>
    this.instance.post('/ConnectionTypes/Add', { title });

  public updateConnectionType = (data: ResponseTypes.ConnectionType): Promise<BaseResponse<{}>> =>
    this.instance.post('/ConnectionTypes/Update', data);

  public deleteConnectionType = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ConnectionTypes/Delete', { id });

  public getConnectionTypes = (): Promise<ResponseTypes.GetConnectionTypesResponse> =>
    this.instance.get('/ConnectionTypes/List');

  public getSpecificConnectionType = (id: number): Promise<ResponseTypes.GetSpecificConnectionTypeResponse> =>
    this.instance.get(`/ConnectionTypes/Get?Id=${id}`);

  // Customer Source Type
  public createCustomerSourceType = (title: string): Promise<BaseResponse<{}>> =>
    this.instance.post('/CustomerSourceType/Add', { title });

  public updateCustomerSourceType = (data: ResponseTypes.CustomerSourceType): Promise<BaseResponse<{}>> =>
    this.instance.post('/CustomerSourceType/Update', data);

  public deleteCustomerSourceType = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/CustomerSourceType/Delete', { id });

  public getCustomerSourceTypes = (): Promise<ResponseTypes.GetCustomerSourceTypesResponse> =>
    this.instance.get('/CustomerSourceType/List');

  public getSpecificCustomerSourceType = (id: number): Promise<ResponseTypes.GetSpecificCustomerSourceTypeResponse> =>
    this.instance.get(`/CustomerSourceType/Get?Id=${id}`);

  // Connection Types
  public createEnvironmentType = (title: string): Promise<BaseResponse<{}>> =>
    this.instance.post('/EnvironmentTypes/Add', { title });

  public updateEnvironmentType = (data: ResponseTypes.EnvironmentType): Promise<BaseResponse<{}>> =>
    this.instance.post('/EnvironmentTypes/Update', data);

  public deleteEnvironmentType = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/EnvironmentTypes/Delete', { id });

  public getEnvironmentTypes = (): Promise<ResponseTypes.GetEnvironmentTypesResponse> =>
    this.instance.get('/EnvironmentTypes/List');

  public getSpecificEnvironmentType = (id: number): Promise<ResponseTypes.GetSpecificEnvironmentTypeResponse> =>
    this.instance.get(`/EnvironmentTypes/Get?Id=${id}`);

  // Effort
  public getEfforts = (data: GetEffortRequestsFormParams): Promise<BaseResponse<ResponseTypes.EffortItem[]>> =>
    this.instance.post('/Effort/List', data);

  public createEffortRequest = (data: CreateEffortRequestPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Effort/Add', data);

  public updateEffortRequest = (data: UpdateEffortRequestPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Effort/Update', data);

  public deleteEffortRequest = (data: number[]): Promise<BaseResponse<{}>> =>
    this.instance.post('/Effort/Delete', data);

  public resendEmail = (effortId: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/Effort/SendMail', { effortId });

  // Effort Confirmation
  public confirmEffort = (data: EffortConfirmationPostParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/EffortConfirmation/Confirm', data);

  public getEffortDetailsForCustomerConfirmation = (
    uniqueKey: string
  ): Promise<BaseResponse<ResponseTypes.EffortDetailsForCustomerConfirmation>> =>
    this.instance.get(`/EffortConfirmation/GetConfirmation?uniqueKey=${uniqueKey}`);

  // Effort Contact
  public getSpecificEffortContact = (id: number): Promise<BaseResponse<ResponseTypes.EffortContactItem>> =>
    this.instance.get(`/EffortContact/Get?effortContactId=${id}`);

  public getEffortContacts = (): Promise<BaseResponse<ResponseTypes.EffortContactItem[]>> =>
    this.instance.get('/EffortContact/List');

  public createEffortContact = (data: CreateEffortContactFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/EffortContact/Add', data);

  public updateEffortContact = (data: UpdateEffortContactFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/EffortContact/Update', data);

  public deleteEffortContact = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post(`/EffortContact/Delete?effortContactId=${id}`);

  // Effort Status Contact
  public getSpecificEffortStatusContact = (id: number): Promise<BaseResponse<ResponseTypes.EffortStatusContactItem>> =>
    this.instance.get(`/EffortStatusContact/Get?effortStatusContactId=${id}`);

  public getEffortStatusContacts = (): Promise<BaseResponse<ResponseTypes.EffortStatusContactItem[]>> =>
    this.instance.get('/EffortStatusContact/List');

  public createEffortStatusContact = (data: CreateEffortStatusContactPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/EffortStatusContact/Add', data);

  public updateEffortStatusContact = (data: UpdateEffortStatusContactPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/EffortStatusContact/Update', data);

  public deleteEffortStatusContact = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post(`/EffortStatusContact/Delete?effortStatusContactId=${id}`);

  // Effort Timeline
  public getEffortTimelineList = (
    startDate: Dayjs | null,
    endDate: Dayjs | null
  ): Promise<BaseResponse<ResponseTypes.EffortLogItem[]>> =>
    this.instance.get(`/EffortTimeline/List?StartDate=${startDate}&EndDate=${endDate}`);

  // Gib Types
  public createGibType = (title: string): Promise<BaseResponse<{}>> => this.instance.post('/GibType/Add', { title });

  public updateGibType = (data: ResponseTypes.GibType): Promise<BaseResponse<{}>> =>
    this.instance.post('/GibType/Update', data);

  public deleteGibType = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/GibType/Delete', { id });

  public getGibTypes = (): Promise<ResponseTypes.GetGibTypesResponse> => this.instance.get('/GibType/List');

  public getSpecificGibType = (id: number): Promise<ResponseTypes.GetSpecificGibTypeResponse> =>
    this.instance.get(`/GibType/Get?Id=${id}`);

  // Holiday
  public getHolidays = (year: number): Promise<BaseResponse<ResponseTypes.HolidayItem[]>> =>
    this.instance.get(`/Holiday/Holidays?year=${year}`);

  // Me
  public me = (): Promise<ResponseTypes.MeResponse> => this.instance.get('/Me');

  public changePassword = (data: ChangePasswordPostParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Me/ChangePassword', data);

  // Permissions
  public getPermissions = (): Promise<ResponseTypes.GetPermissionsResponse> => this.instance.get('/Permissions');

  // Project Groups
  public createProjectGroup = (title: string): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectGroups/Add', { title });

  public updateProjectGroup = (data: ResponseTypes.ProjectGroup): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectGroups/Update', data);

  public deleteProjectGroup = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectGroups/Delete', { id });

  public getProjectGroups = (): Promise<ResponseTypes.GetProjectGroupsResponse> =>
    this.instance.get('/ProjectGroups/List');

  public getSpecificProjectGroup = (id: number): Promise<ResponseTypes.GetSpecificProjectGroupResponse> =>
    this.instance.get(`/ProjectGroups/Get?Id=${id}`);

  // Project Report
  public getProjectReports = (): Promise<ResponseTypes.GetProjectReportsResponse> =>
    this.instance.get('/ProjectReport/List');

  // Project Report Comment
  public addCommentToProjectReport = (
    projectReportId: number,
    commentText: string
  ): Promise<BaseResponse<{ id: number }>> =>
    this.instance.post('/ProjectReportComment/Add', { projectReportId, commentText });

  public deleteCommentFromProjectReport = (commentId: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectReportComment/Delete', { id: commentId });

  // Projects
  public createProject = (data: CreateProjectFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Projects/Add', data);

  public updateProject = (data: UpdateProjectFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Projects/Update', data);

  public deleteProject = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/Projects/Delete', { id });

  public getProjects = (): Promise<ResponseTypes.GetProjectsResponse> => this.instance.get('/Projects/List');

  public getSpecificProject = (id: number): Promise<ResponseTypes.GetSpecificProjectResponse> =>
    this.instance.get(`/Projects/Get?Id=${id}`);

  // Project Task
  public getSpecificProjectStep = (id: number): Promise<BaseResponse<ProjectStepItem>> =>
    this.instance.get(`/ProjectTask/Get?projectTaskId=${id}`);

  public getProjectSteps = (data: {
    statusType: number | null;
    projectId: number | null;
    bookId: number | null;
    contactId: number | null;
  }): Promise<BaseResponse<ProjectStepItem[]>> => this.instance.post('/ProjectTask/List', data);

  public createProjectStep = (data: CreateProjectStepPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTask/Add', data);

  public createProjectSteps = (data: AddProjectStepListItem[]): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTask/AddList', { projectTasks: data });

  public updateProjectStep = (data: UpdateProjectStepPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTask/Update', data);

  public deleteProjectStep = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post(`/ProjectTask/Delete?projectTaskId=${id}`);

  // Project Task Base
  public getSpecificProductProjectPlan = (id: number): Promise<BaseResponse<ResponseTypes.ProductProjectPlanItem>> =>
    this.instance.get(`/ProjectTaskBase/Get?projectTaskBaseId=${id}`);

  public getProductProjectPlans = (postData: {
    projectId: number | null;
  }): Promise<BaseResponse<ResponseTypes.ProductProjectPlanItem[]>> =>
    this.instance.post('/ProjectTaskBase/List', postData);

  public createProductProjectPlan = (data: CreateProductProjectPlanPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTaskBase/Add', data);

  public updateProductProjectPlan = (data: UpdateProductProjectPlanPostFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTaskBase/Update', data);

  public deleteProductProjectPlan = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post(`/ProjectTaskBase/Delete?projectTaskBaseId=${id}`);

  // Project Task Comment
  public addCommentToProjectStep = (
    projectTaskId: number,
    commentText: string
  ): Promise<BaseResponse<{ id: number }>> =>
    this.instance.post('/ProjectTaskComment/Add', { projectTaskId, commentText });

  public deleteCommentFromProjectStep = (commentId: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/ProjectTaskComment/Delete', { id: commentId });

  // Report
  public getBooks = (): Promise<ResponseTypes.GetBooksResponse> => this.instance.get('/Report/Books');

  // Roles
  public getRoles = (): Promise<ResponseTypes.GetRolesResponse> => this.instance.get('/Roles');

  public createRole = (data: CreateRoleFormParams): Promise<BaseResponse<{}>> => this.instance.post('/Roles', data);

  public getSpecificRole = (id: number): Promise<ResponseTypes.GetSpecificRoleResponse> =>
    this.instance.get(`/Roles/${id}`);

  public updateRole = (data: UpdateRoleFormParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/Roles/Update', data);

  public deleteRole = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/Roles/Delete', { id });

  // Sales Stage Type
  public createSalesStageType = (title: string): Promise<BaseResponse<{}>> =>
    this.instance.post('/SalesStageType/Add', { title });

  public updateSalesStageType = (data: ResponseTypes.SalesStageType): Promise<BaseResponse<{}>> =>
    this.instance.post('/SalesStageType/Update', data);

  public deleteSalesStageType = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/SalesStageType/Delete', { id });

  public getSalesStageTypes = (): Promise<ResponseTypes.GetSalesStageTypesResponse> =>
    this.instance.get('/SalesStageType/List');

  public getSpecificSalesStageType = (id: number): Promise<ResponseTypes.GetSpecificSalesStageTypeResponse> =>
    this.instance.get(`/SalesStageType/Get?Id=${id}`);

  // Table Schema
  public getTableSchemaByPageName = (pageName: string): Promise<BaseResponse<ResponseTypes.TableSchema>> =>
    this.instance.get(`/TableSchema/${pageName}`);

  public createTableTemplate = (data: ResponseTypes.CreateTableTemplateDto): Promise<BaseResponse<{}>> =>
    this.instance.post('/TableSchema', data);

  public deleteTableTemplate = (id: number): Promise<BaseResponse<{}>> =>
    this.instance.post('/TableSchema/Delete', { id });

  // Timeline
  public getTimelineList = (
    startDate: Dayjs | null,
    endDate: Dayjs | null
  ): Promise<ResponseTypes.GetTimelineListResponse> =>
    this.instance.get(`/Timeline/List?StartDate=${startDate}&EndDate=${endDate}`);

  // Used Banks
  public createUsedBank = (title: string): Promise<BaseResponse<{}>> => this.instance.post('/UsedBanks/Add', { title });

  public updateUsedBank = (data: ResponseTypes.UsedBank): Promise<BaseResponse<{}>> =>
    this.instance.post('/UsedBanks/Update', data);

  public deleteUsedBank = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/UsedBanks/Delete', { id });

  public getUsedBanks = (): Promise<ResponseTypes.GetUsedBanksResponse> => this.instance.get('/UsedBanks/List');

  public getSpecificUsedBank = (id: number): Promise<ResponseTypes.GetSpecificUsedBankResponse> =>
    this.instance.get(`/UsedBanks/Get?Id=${id}`);

  // User
  public getUsers = (): Promise<ResponseTypes.GetUsersResponse> => this.instance.get('/User/List');

  public getSpecificUser = (id: number): Promise<ResponseTypes.GetSpecificUserResponse> =>
    this.instance.get(`/User/Get?Id=${id}`);

  public createUser = (data: CreateUserFormParams): Promise<BaseResponse<{}>> => this.instance.post('/User/Add', data);

  public assignProject = (data: AssignProjectPostParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/User/Assign', data);

  public updateUser = (data: UpdateUserPostParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/User/Update', data);

  public deleteUser = (id: number): Promise<BaseResponse<{}>> => this.instance.post('/User/Delete', { id });

  // Personnel Holiday
  public getPersonnelHolidays = (year: number): Promise<BaseResponse<ResponseTypes.PersonnelHolidayListResponse>> =>
    this.instance.get(`/PersonnelHoliday/List?Year=${year}`);

  public createPersonnelHoliday = (data: ResponseTypes.InsertPersonnelHolidayParams): Promise<BaseResponse<{}>> =>
    this.instance.post('/PersonnelHoliday/Insert', data);

  public deletePersonnelHoliday = (data: { processNo: string; entryDate: string }): Promise<BaseResponse<{}>> =>
    this.instance.post('/PersonnelHoliday/Delete', data);

  public getPersonnelHolidayExample = (): Promise<Blob> =>
    this.instance.get('/PersonnelHoliday/DownloadForm', { responseType: 'blob' });

  public getActivityBoardPermissions = (): Promise<BaseResponse<ResponseTypes.ActivityBoardPermissionItem[]>> =>
    this.instance.get('/PersonnelHoliday/GetActivityBoardPermissions');

  public getPersonnelHolidayBalances = (): Promise<BaseResponse<ResponseTypes.PersonnelHolidayBalanceItem[]>> =>
    this.instance.get('/PersonnelHoliday/GetPersonnelHolidayBalances');
}

export default Service.getInstance();
